import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../data/cloud-data.js';
import { Configuration } from '../../data/configuration.js';
import { CameraController, CameraViews } from '../../camera-controller.js';
import { ExteriorConfigurator } from '../../configurator/exterior-configurator.js';
import { VehicleView } from '../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../view-content.js';
import SelectedSectionItem from '../ui/selected-section-item.tsx';
import { AppContext } from '../app.tsx';
import ViewContentRows from '../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface ExteriorTextilesViewProps {
	exteriorConfigurator: ExteriorConfigurator;
	cameraController: CameraController;
	onBackButtonClicked: () => void;
}

interface ExteriorTextilesViewContextType {
	selectedHoodItemId: string;
	handleHoodItemClicked: (id: string) => void;
	selectedWingBeadingItemId: string;
	handleWingBeadingItemClicked: (id: string) => void;
	toggleHood: () => void;
	hoodUp: boolean;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function ExteriorTextilesView(props: ExteriorTextilesViewProps) {

	// get the app context and ensure it is valid.
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('AppContext must be used within the main App component');
	}

	// initial values.
	const initialHoodItemId = Configuration.hoodStyleOption;
	const initialHoodItemName = () => getItemName(initialHoodItemId);
	const initialWingBeadingItemId = Configuration.wingBeadingOption;
	const initialWingBeadingItemName = () => getWingBeadingItemName(initialWingBeadingItemId);

	// states.
	const [selectedHoodItemId, setSelectedHoodItemId] = useState(initialHoodItemId);
	const [selectedHoodItemName, setSelectedHoodItemName] = useState(initialHoodItemName);
	const [selectedWingBeadingItemId, setSelectedWingBeadingItemId] = useState(initialWingBeadingItemId);
	const [selectedWingBeadingItemName, setSelectedWingBeadingItemName] = useState(initialWingBeadingItemName);
	
	// handlers.

	const handleHoodItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedHoodItemId(id);
		setSelectedHoodItemName(getItemName(id));

		// update the configuration.
		Configuration.hoodStyleOption = id;
		Configuration.updateCosts();

		// select the hood style on the car.
		props.exteriorConfigurator.selectHoodStyle(id);

		// transition the camera to the relevant viewpoint.
		props.cameraController.changeCameraView(CameraViews.HoodView);
	};

	const handleWingBeadingItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedWingBeadingItemId(id);
		setSelectedWingBeadingItemName(getWingBeadingItemName(id));

		// update the configuration.
		Configuration.wingBeadingOption = id;
		Configuration.updateCosts();

		// select the wing beading style on the car.
		props.exteriorConfigurator.selectWingBeadingOption(id);

		// transition the camera to the relevant viewpoint.
		props.cameraController.changeCameraView(CameraViews.WingBeadingView);
	};


	const toggleHood = () => {
		context.exteriorConfigurator.selectSoftTopState(!context.hoodUp);
		context.setHoodUp(!context.hoodUp);
	}

	// return the HTML.
	return (
		<ExteriorTextilesViewContext.Provider value={{ selectedHoodItemId, handleHoodItemClicked, selectedWingBeadingItemId, handleWingBeadingItemClicked, toggleHood, hoodUp: context.hoodUp }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-exterior-textiles">
							EXTERIOR TEXTILES
						</h2>
					</div>

					<SectionPanel
						subHeading={'HOOD'}
						modalContent={CloudData.getModalContent(selectedHoodItemId, CloudData.hoodStyleOptions)}
					>
						<SelectedSectionItem
							itemName={selectedHoodItemName}
							sectionPrice={CloudData.getPrice(selectedHoodItemId, CloudData.hoodStyleOptions, { ref: false })}
						/>
						<HoodStyleItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'WING BEADING'}
						modalContent={CloudData.getModalContent(selectedWingBeadingItemId, CloudData.wingBeadingOptions)}
					>
						<SelectedSectionItem
							itemName={selectedWingBeadingItemName}
							sectionPrice={CloudData.getPrice(selectedWingBeadingItemId, CloudData.wingBeadingOptions, { ref: false })}
						/>
						<WingBeadingItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'HOOD VIEW'}
					>
						<SelectedSectionItem
							itemName={'Click to view hood up/down'}
						/>
						<InlineSettings />
					</SectionPanel>

				</div>
			</VehicleView>
		</ExteriorTextilesViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const ExteriorTextilesViewContext = createContext<ExteriorTextilesViewContextType | undefined>(undefined);

function HoodStyleItems() {
	return (
		<ViewContentRows>
			<HoodStyleItem id={'H001'} imageFilePath={'black'} />
			<HoodStyleItem id={'H002'} imageFilePath={'green'} />
			<HoodStyleItem id={'H003'} imageFilePath={'blue'} />
			<HoodStyleItem id={'H004'} imageFilePath={'grey'} />
			<HoodStyleItem id={'H005'} imageFilePath={'beige'} />
			<HoodStyleItem id={'H006'} imageFilePath={'maroon'} />
			<HoodStyleItem id={'H007'} imageFilePath={'brown'} />
		</ViewContentRows>
	);
};

function HoodStyleItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(ExteriorTextilesViewContext);
	if (!context) {
		throw new Error('ExteriorTextilesViewContext must be used within a ExteriorTextilesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/hood/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedHoodItemId}
			onItemClicked={context.handleHoodItemClicked}
		/>
	);
}

function WingBeadingItems() {
	return (
		<ViewContentRows maxRowLength={10}>
			<WingBeadingItem id={'WB001'} imageFilePath={'black'} />
			<WingBeadingItem id={'WB002'} imageFilePath={'blue'} />
			<WingBeadingItem id={'WB003'} imageFilePath={'cream'} />
			<WingBeadingItem id={'WB004'} imageFilePath={'green'} />
			<WingBeadingItem id={'WB005'} imageFilePath={'grey'} />
			<WingBeadingItem id={'WB006'} imageFilePath={'maroon'} />
			<WingBeadingItem id={'WB007'} imageFilePath={'red'} />
			<WingBeadingItem id={'WB008'} imageFilePath={'sand'} />
			<WingBeadingItem id={'WB009'} imageFilePath={'stone'} />
			<WingBeadingItem id={'WB010'} imageFilePath={'white'} />
		</ViewContentRows>
	);
};

function WingBeadingItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(ExteriorTextilesViewContext);
	if (!context) {
		throw new Error('ExteriorTextilesViewContext must be used within a ExteriorTextilesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getWingBeadingItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/wing-beading/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedWingBeadingItemId}
			onItemClicked={context.handleWingBeadingItemClicked}
		/>
	);
}


function InlineSettings() {

	// get the context and ensure it is valid.
	const context = useContext(ExteriorTextilesViewContext);
	if (!context) {
		throw new Error('ExteriorTextilesViewContext must be used within a ExteriorTextilesView component');
	}

	return (
		<ViewContentRows>
			<SectionItem
				id={'hood-toggle'}
				itemName={'Toggle Hood Up/Down'}
				imageFilePath={'assets/images/icons/inline-hood-toggle.svg'}
				isSelected={context.hoodUp}
				onItemClicked={context.toggleHood}
			/>
		</ViewContentRows>
	);
};

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.hoodStyleOptions);
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getWingBeadingItemName(id: string): string {
	return CloudData.getName(id, CloudData.wingBeadingOptions);
}
